import { Box, Chip, Paper, Typography } from '@mui/material';

interface ChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  disabled?: boolean;
  badge?: string;
  variant: 'large' | 'medium' | 'small';
}

const ChoiceCard = ({ icon, title, subtitle, onClick, disabled = false, badge, variant }: ChoiceCardProps) => {
  const isLarge = variant === 'large';
  const isSmall = variant === 'small';

  return (
    <Paper
      elevation={disabled ? 1 : 2}
      onClick={disabled ? undefined : onClick}
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: isLarge ? { xs: 140, sm: 160 } : isSmall ? { xs: 48, sm: 56 } : { xs: 100, sm: 120 },
        p: isLarge ? { xs: 2, sm: 3 } : isSmall ? { xs: 1, sm: 1.5 } : { xs: 1.5, sm: 2 },
        borderRadius: isLarge ? 4 : isSmall ? 2 : 3,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        filter: disabled ? 'grayscale(100%)' : 'none',
        transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
        '&:hover': disabled
          ? {}
          : {
              boxShadow: 4,
              backgroundColor: 'rgba(134, 222, 105, 0.06)',
            },
      }}
    >
      {badge && (
        <Chip
          label={badge}
          size="small"
          color="secondary"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: isLarge ? 1.5 : isSmall ? 0.5 : 1,
          '& .MuiSvgIcon-root': {
            fontSize: isLarge ? { xs: 40, sm: 48 } : isSmall ? { xs: 22, sm: 26 } : { xs: 30, sm: 36 },
            color: 'primary.main',
          },
        }}
      >
        {icon}
      </Box>

      <Typography
        variant={isLarge ? 'h5' : 'h6'}
        component="span"
        sx={{
          fontWeight: 600,
          textAlign: 'center',
          fontSize: isLarge
            ? { xs: '1.15rem', sm: '1.5rem' }
            : isSmall
              ? { xs: '0.85rem', sm: '0.95rem' }
              : { xs: '0.95rem', sm: '1.1rem' },
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.5,
            textAlign: 'center',
            fontSize: isLarge
              ? { xs: '0.8rem', sm: '0.875rem' }
              : isSmall
                ? { xs: '0.7rem', sm: '0.75rem' }
                : { xs: '0.75rem', sm: '0.8rem' },
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

export default ChoiceCard;
